import {
    Avatar,
    Box,
    Button,
    Divider,
    Flex,
    HStack,
    Image,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Text,
    Textarea,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { colors } from "../../../styles";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createReply, getMyProfile } from "../../../api";
import { IMyProfile } from "../../../types";

const MAX_CONTENT_LENGTH = 2000;

interface ReplyToModalProps {
    isOpen: boolean;
    onClose: () => void;
    slug: string;
    onPostStart?: () => void;
    /** API 응답 완료 시 호출. 데이터 갱신은 ChatDetail 쪽에서 처리 */
    onPostDone?: () => void;
    onPostError?: () => void;

    comment: {
        id: string;
        profile: {
            id: string;
            avatar?: string;
            nickname: string;
        };
        content: string;
    };
}

export default function ReplyToModal({
    onPostStart,
    onPostDone,
    onPostError,
    ...props
}: ReplyToModalProps) {

    /* shortContent ~ */

    const shortContent = props.comment.content.length > 100 ? props.comment.content.slice(0, 100) + "..." : props.comment.content;

    /* query */

    const toast = useToast();

    const { data: myProfileData } = useQuery<IMyProfile>({
        queryKey: ["getMyProfile"],
        queryFn: getMyProfile,
    });

    /* content ~ */

    const [content, setContent] = useState("");
    const canPost = content.trim().length > 0;

    /* useEffect: 모달 종료 시 초기화 */

    useEffect(() => {
        if (!props.isOpen) {
            setContent("");
        }
    }, [props.isOpen]);

    /* createReplyMutation */

    const createReplyMutation = useMutation({
        mutationFn: createReply,
        onSuccess: () => {
            props.onClose();
            onPostDone?.();
        },
        onError: (error) => {
            console.error(error);
            onPostError?.();
            toast({
                title: "답글 게시에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* handlePost: 게시 */

    const handlePost = () => {
        if (!canPost || createReplyMutation.isPending) return;
        const payload = {
            slug: props.slug,
            commentId: props.comment.id,
            content: content.trim(),
        };
        onPostStart?.();
        createReplyMutation.mutate(payload);
    };



    /* render */

    return (
        <Modal
            motionPreset="slideInBottom"
            size="2xl"
            isOpen={props.isOpen}
            onClose={props.onClose}
        >
            <ModalOverlay />
            <ModalContent borderRadius={16} overflow="hidden">
                <ModalHeader
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    borderBottom="0.2px solid"
                    borderColor={colors.g4}
                    padding={3}
                >
                    <Text
                        as="button"
                        type="button"
                        fontSize={16}
                        fontWeight={500}
                        color={colors.black}
                        cursor="pointer"
                        onClick={props.onClose}
                    >
                        취소
                    </Text>
                </ModalHeader>

                <ModalBody padding={5}>

                    {/* 원문 채팅 내용 */}

                    <VStack width="100%" alignItems="flex-start" spacing={2} marginBottom={4}>
                    <HStack justifyContent="flex-start" alignItems="center" gap={2}>
                        {props.comment.profile.avatar ? (
                            <Image
                                src={props.comment.profile.avatar}
                                alt={`${props.comment.profile.nickname} 프로필 이미지`}
                                width={4}
                                height={4}
                                border="0.5px solid"
                                borderColor={colors.g4}
                                borderRadius="full"
                                objectFit="cover"
                                flexShrink={0}
                            />
                        ) : (
                            <Avatar size="sm" backgroundColor={colors.g4} flexShrink={0} />
                        )}
                        <Text fontSize={14} fontWeight={600} color={colors.g1}>
                            {props.comment.profile.nickname}
                        </Text>
                    </HStack>
                        <Text
                            width="100%"
                            fontSize={14}
                            fontWeight={400}
                            color={colors.g3}
                            noOfLines={1}
                        >
                            {shortContent}
                        </Text>
                        <Divider borderColor={colors.g4} marginY={1} />
                    </VStack>

                    {/* 댓글 입력 영역 */}

                    <HStack alignItems="flex-start" gap={3}>
                        {myProfileData?.avatar ? (
                            <Image
                                src={myProfileData?.avatar}
                                alt={`${myProfileData?.nickname} 프로필 이미지`}
                                width={10}
                                height={10}
                                borderRadius="full"
                                objectFit="cover"
                                flexShrink={0}
                            />
                        ) : (
                            <Avatar size="sm" backgroundColor={colors.g4} flexShrink={0} />
                        )}

                        <Stack flex={1} gap={3} alignItems="flex-start">
                            <Text fontSize={16} fontWeight={700} color={colors.g1}>
                                {myProfileData?.nickname}
                            </Text>
                            <Textarea
                                value={content}
                                onChange={(event) => setContent(event.target.value)}
                                placeholder={`${props.comment.profile.nickname} 님에게 댓글 남기기...`}
                                maxLength={MAX_CONTENT_LENGTH}
                                variant="unstyled"
                                fontSize={16}
                                fontWeight={400}
                                color={colors.g1}
                                minHeight={120}
                                resize="none"
                                padding={0}
                                _placeholder={{ color: colors.g3 }}
                            />
                        </Stack>
                    </HStack>
                </ModalBody>

                <ModalFooter
                    borderTop="0.2px solid"
                    borderColor={colors.g4}
                    padding={3}
                >
                    <HStack width="100%" justifyContent="space-between" alignItems="center">
                        <Box />
                        <Button
                            size="sm"
                            borderRadius="full"
                            paddingX={6}
                            variant="ghost"
                            color={canPost ? colors.blue : colors.g3}
                            _hover={{
                                backgroundColor: canPost ? colors.light : colors.g5,
                            }}
                            _active={{
                                backgroundColor: canPost ? colors.light : colors.g5,
                            }}
                            isDisabled={!canPost}
                            isLoading={createReplyMutation.isPending}
                            onClick={handlePost}
                        >
                            게시
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
